/**
 * Shared canvas-group ordering + the single ungrouped sentinel.
 *
 * `UNGROUPED_KEY` is the Convex / filter / presence / preview encoding.
 * URL segments use `ungrouped`; the list table uses the display string
 * `'Ungrouped'`. Do not add a fourth sentinel — convert at the adapter.
 *
 * Manual order matches the /emails canvas: named groups sort by
 * `orderKey` DESC (larger key leftmost); ungrouped (no `orderKey`) sorts
 * by `createdAt` ASC (older leftmost). `sortDir` is ignored.
 */

export const UNGROUPED_KEY = '__ungrouped__'

/**
 * Reserved folder names that must never become a real `groups` row.
 * Case-insensitive so `Ungrouped` / `UNGROUPED` / `__ungrouped__` cannot
 * mint a colliding named group. Id sentinels stay exact — see
 * `isPublicUngroupedGroupId` / `isUngroupedGroupSentinel`.
 */
export function isReservedGroupName(name: string): boolean {
  const normalized = name.trim().toLowerCase()
  return normalized === 'ungrouped' || normalized === UNGROUPED_KEY
}

export type ManualOrderChat = {
  orderKey?: string | null
  createdAt?: string | null
}

/**
 * Within-row ordering rules (newest emails rightmost):
 *
 * 1. Among orderKey-bearing rows, sort orderKey DESCENDING. Convex assigns
 *    position=0 (newest insert) the smallest orderKey, so descending sort
 *    places the newest email at the rightmost slot — i.e. new emails are
 *    appended to the end of their row.
 *
 * 2. No-orderKey rows sort to the RIGHT of orderKey-bearing rows. As of the
 *    atomic-grouping change, `upsertStreamingEmail` writes the
 *    `emailGrouping` row in the same Convex transaction as the `emails` row
 *    insert, so a missing orderKey is a transient/historical state rather
 *    than the routine streaming case. Keeping these to the right matches
 *    rule (1)'s "newest rightmost" semantics for the brief window before
 *    the orderKey lands.
 *
 * 3. Among no-orderKey rows (ungrouped), older `createdAt` is leftmost.
 */
export function compareChatsByManualOrder(
  a: ManualOrderChat,
  b: ManualOrderChat
): number {
  const hasAKey = a.orderKey != null
  const hasBKey = b.orderKey != null

  if (hasAKey && hasBKey) {
    return (b.orderKey ?? '').localeCompare(a.orderKey ?? '')
  }
  // No-orderKey rows (just-streaming, pre-eager-grouping) go last (rightmost).
  if (!hasAKey && hasBKey) {
    return 1
  }
  if (hasAKey && !hasBKey) {
    return -1
  }

  const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
  const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
  return aTime - bTime
}

/** Preview / filter / presence key for a denormalized `emails.groupId`. */
export function previewGroupKey(groupId: string | undefined): string {
  return groupId ?? UNGROUPED_KEY
}
